/**
 * Application Marketplace API Client
 *
 * API client functions for all Application Marketplace endpoints.
 * Extends BaseApiClient for authentication, error handling, and type-safe requests.
 */

import {
  ApplicationStatus,
  type Application,
  type ApplicationSession,
  type OrganizationAppLimit,
  type ApplicationUsageStats,
  type CreateApplicationRequest,
  type UpdateApplicationRequest,
  type CreateSessionRequest,
  type EnableApplicationRequest,
  type ListApplicationsResponse,
  type ListSessionsResponse,
  type UploadAssetResponse,
  type NameAvailabilityResponse,
  type ApplicationFilters,
  type SessionFilters,
} from '@gw/ui/types';

// GW-6533: Validate orgId at API client boundary so frontend regressions
// throw a distinct InvalidOrgIdError before the network call leaves the
// browser, instead of producing a noisy 4xx after a round-trip.
// GW-6534: Read currentOrg.id from the Zustand store as the canonical source
// of truth for the E2E mock path.
import { getCurrentOrgIdFromStore } from '@/stores/orgIdAccessor';
import { assertOrgId } from '@/types/orgId';

import { ApiError } from './apiError';
import { BaseApiClient } from './baseApiClient';

// ============================================================================
// E2E Mock Support
// ============================================================================

/**
 * E2E Mock Store for applications
 * When VITE_E2E_TEST_MODE=true, we use a triple-storage approach:
 * 1. Module-level cache for fast access during client-side navigation
 * 2. localStorage for persistence across page reloads
 * 3. window.__E2E_MOCK_APPLICATIONS__ for browser test injection fallback
 *
 * This ensures reliable mock data access across page transitions.
 */
const E2E_MOCK_STORAGE_KEY = 'e2e-mock-applications';
const E2E_WINDOW_KEY = '__E2E_MOCK_APPLICATIONS__';

interface E2EMockStore {
  applications: Application[];
}

// Module-level cache - persists during client-side navigation
let e2eMockCache: E2EMockStore | null = null;

// Extend Window interface for E2E mock storage
declare global {
  interface Window {
    [E2E_WINDOW_KEY]?: E2EMockStore;
    __injectE2EMockApps?: (apps: Application[]) => void;
  }
}

/**
 * Get E2E mock store using triple-storage approach
 * Priority: cache > window global > localStorage > empty store
 */
function getE2EMockStore(): E2EMockStore {
  // Use cache if available (fastest path)
  if (e2eMockCache && e2eMockCache.applications.length > 0) {
    return e2eMockCache;
  }

  // Check window global (browser test-injected data)
  try {
    if (typeof window !== 'undefined' && window[E2E_WINDOW_KEY]?.applications?.length) {
      const windowStore = window[E2E_WINDOW_KEY];
      e2eMockCache = windowStore;
      // Also sync to localStorage
      try {
        localStorage.setItem(E2E_MOCK_STORAGE_KEY, JSON.stringify(windowStore));
      } catch {
        // Ignore localStorage errors
      }
      return e2eMockCache;
    }
  } catch {
    // Window access might fail in some contexts
  }

  // Initialize from localStorage on first access or after page reload
  try {
    const stored = localStorage.getItem(E2E_MOCK_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Populate cache
      const store = parsed as E2EMockStore;
      e2eMockCache = store;
      // Also sync to window global for cross-navigation access
      if (typeof window !== 'undefined') {
        window[E2E_WINDOW_KEY] = store;
      }
      return store;
    }
  } catch (e) {
    console.error('[E2E MockStore] Parse error:', e);
  }

  // Initialize empty store (but don't cache it - allow retries)
  return { applications: [] };
}

/**
 * Save E2E mock store to cache, localStorage, AND window global
 * This ensures data is available regardless of which storage mechanism works
 */
function saveE2EMockStore(store: E2EMockStore): void {
  // Always update the cache first (immediate consistency)
  e2eMockCache = store;

  // Save to window global (reliable for browser tests)
  try {
    if (typeof window !== 'undefined') {
      window[E2E_WINDOW_KEY] = store;
    }
  } catch {
    // Ignore window errors
  }

  // Also persist to localStorage for page reload scenarios
  try {
    const json = JSON.stringify(store);
    localStorage.setItem(E2E_MOCK_STORAGE_KEY, json);
  } catch (e) {
    console.error('[E2E MockStore] localStorage save error:', e);
  }
}

/**
 * Initialize E2E mock injection helper
 * This creates a window function that browser tests can call to inject mock apps
 */
function initE2EMockInjection(): void {
  if (typeof window !== 'undefined' && isE2ETestMode()) {
    window.__injectE2EMockApps = (apps: Application[]) => {
      const store: E2EMockStore = { applications: apps };
      e2eMockCache = store;
      window[E2E_WINDOW_KEY] = store;
      try {
        localStorage.setItem(E2E_MOCK_STORAGE_KEY, JSON.stringify(store));
      } catch {
        // Ignore localStorage errors
      }
    };
  }
}

// Initialize E2E mock injection on module load
initE2EMockInjection();

function isE2ETestMode(): boolean {
  return import.meta.env.VITE_E2E_TEST_MODE === 'true';
}

// ============================================================================
// TOS (Terms of Service) types — GW-11433/GW-11452, PRP-MKTPL-016.
//
// Canonical shape shared across applications/datasets/services/missions TOS
// (mirrors gw-datasets-api's split-endpoint shape from GW-11440/GW-11453).
// Defined locally rather than imported from `@gw/ui/types` because that
// package's `TOSAcceptanceStatus`/`TOSAcceptance`/`OrgTOSOverview` types
// still reflect the pre-GW-11433 flat-boolean shape
// (`userAccepted`/`orgAccepted`/`canProceed` with no staleness fields) —
// updating them is a separate `@gw/ui` PR. See
// `DatasetTOSAcceptanceStatus`/`DatasetTOSAcceptResponse` in `api/datasets.ts`
// for the identical precedent.
// ============================================================================

export type ApplicationTOSRequiredLevel = 'none' | 'user' | 'org' | 'both';

export interface ApplicationTOSAcceptanceInfo {
  accepted: boolean;
  acceptanceType: 'user' | 'org';
  acceptedVersion: number;
  acceptedAt?: string;
}

/**
 * GET /v1/applications/{appId}/tos/acceptance response.
 * userAcceptance/orgAcceptance are null when no non-revoked acceptance
 * exists for that actor type — never {accepted: false}.
 */
export interface ApplicationTOSAcceptanceStatus {
  itemId: string;
  tosRequiredLevel: ApplicationTOSRequiredLevel;
  tosVersion: number | null;
  userAcceptance: ApplicationTOSAcceptanceInfo | null;
  orgAcceptance: ApplicationTOSAcceptanceInfo | null;
  userNeedsReaccept: boolean;
  orgNeedsReaccept: boolean;
  canProceed: boolean;
}

/**
 * Response body for POST .../tos/accept/user and .../tos/accept/org.
 */
export interface ApplicationTOSAcceptance {
  acceptanceType: 'user' | 'org';
  tosVersion: number;
  acceptedAt: string;
}

/**
 * FR-29 per-application TOS acceptance audit list
 * (GET /v1/applications/{appId}/tos/acceptances, org_admin/global_admin only).
 */
export interface ApplicationTOSAcceptancesResponse {
  items: Array<{
    userId: string;
    userEmail?: string;
    acceptanceType: 'user' | 'org';
    tosVersion: number;
    acceptedAt: string;
    isStale: boolean;
  }>;
  summary: {
    total: number;
    accepted: number;
    stale: number;
  };
}

/**
 * FR-29 cross-application org TOS overview
 * (GET /v1/applications/tos/org-overview, org_admin/global_admin only).
 */
export interface ApplicationOrgTOSOverviewResponse {
  items: Array<{
    appId: string;
    appName: string;
    category?: string;
    iconUrl?: string;
    tosRequiredLevel: ApplicationTOSRequiredLevel;
    tosVersion: number;
    orgAccepted: boolean;
    orgNeedsReaccept: boolean;
  }>;
  summary: {
    total: number;
    accepted: number;
    pending: number;
    needsReaccept: number;
  };
}

/**
 * GW-474: Transform helper functions for snake_case <-> camelCase conversion
 * Backend uses snake_case, frontend uses camelCase
 */
class ApplicationsApiClient extends BaseApiClient {
  constructor() {
    // GW-8271: suppressSessionExpired — application lookups/edits (e.g.
    // fetchApplicationById on the edit-application route) return 401/403/404
    // for a bad app ID, an app the caller's org doesn't own, or a private
    // app the caller can't see — not for session expiry. The default
    // BaseApiClient behavior treats any 401/403 from a requiresAuth client
    // as a session-invalid signal and force-logs the user out via
    // authEvents.emit('session-expired'), which tears down the entire React
    // app (including AppLayout/LayoutShell) and redirects to /auth/login —
    // this is what made the application editor appear to render "without
    // sidebar/header chrome" (GW-8271): the user was actually bounced to the
    // unauthenticated login page, not missing a layout wrapper. Same root
    // cause and fix pattern as GW-8262 (datasetsApi, missionsDirectApi).
    // Genuine session expiry is still caught by devStackAuth.util's
    // getAuthToken() refresh-failure path, independent of this setting.
    super({ suppressSessionExpired: true });
  }

  private uploadFile(path: string, file: File): Promise<UploadAssetResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.upload<UploadAssetResponse>(path, formData);
  }

  // ============================================================================
  // Application Management APIs
  // ============================================================================

  /**
   * 1. Create Application
   * POST /applications
   * Authorization: app_admin role
   *
   * E2E MODE: Creates a mock application in localStorage
   */
  async createApplication(data: CreateApplicationRequest): Promise<Application> {
    // E2E TEST MODE: Create mock application
    if (isE2ETestMode()) {
      const store = getE2EMockStore();
      // GW-6534: Try the Zustand store first (canonical source of truth);
      // fall back to localStorage and the persisted state blob for E2E
      // bootstrapping when the store has not yet hydrated.
      let orgId: string | null = getCurrentOrgIdFromStore();
      if (!orgId) {
        orgId = localStorage.getItem('gw-current-org-id');
      }
      if (!orgId) {
        try {
          const storage = localStorage.getItem('gw-storage');
          if (storage) {
            const parsed = JSON.parse(storage);
            orgId = parsed?.state?.currentOrg?.id || null;
          }
        } catch {
          // Ignore parse errors
        }
      }
      orgId = orgId || 'test-org-001'; // Default to test org ID used in E2E setup
      const newApp: Application = {
        appId: `e2e-app-${Date.now()}`,
        providerOrgId: orgId,
        name: data.name,
        description: data.description || '',
        longDescription: data.longDescription || undefined,
        applicationUrl: data.applicationUrl || 'https://example.com',
        signupUrl: data.signupUrl || null,
        developerWebsite: data.developerWebsite,
        iconUrl: null,
        screenshots: [],
        baseCostPerSession: data.baseCostPerSession || 1,
        sessionDurationMinutes: data.sessionDurationMinutes || 30,
        tosRequired: data.tosRequired || 'none',
        tosContent: null,
        minimumPrivyLevel: data.minimumPrivyLevel ?? 0,
        acceptRenewalDueCredentials: data.acceptRenewalDueCredentials ?? false,
        status: ApplicationStatus.Hidden,
        visibility: data.visibility || 'private',
        markupPercent: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'e2e-test-user',
      };
      store.applications.push(newApp);
      saveE2EMockStore(store);
      return newApp;
    }

    return this.post<Application>('/applications', data);
  }

  /**
   * 2. List Applications
   * GET /applications
   * Authorization: Any authenticated user
   *
   * E2E MODE: Returns mock applications from localStorage
   */
  async listApplications(filters?: ApplicationFilters): Promise<ListApplicationsResponse> {
    // E2E TEST MODE: Return mock applications
    if (isE2ETestMode()) {
      const store = getE2EMockStore();
      let apps = [...store.applications];

      // Apply basic filtering
      if (filters?.search) {
        const searchLower = filters.search.toLowerCase();
        apps = apps.filter(
          (app) =>
            app.name.toLowerCase().includes(searchLower) ||
            app.description?.toLowerCase().includes(searchLower)
        );
      }

      return {
        applications: apps,
        pagination: {
          limit: 100,
          hasMore: false,
          total: apps.length,
        },
      };
    }

    const queryParams = filters ? this.buildQueryString(filters as Record<string, string>) : '';
    return this.get<ListApplicationsResponse>(`/applications${queryParams}`);
  }

  /**
   * 3. Get Application Details
   * GET /applications/{app_id}
   * Authorization: Any authenticated user
   *
   * E2E MODE: Returns mock application from localStorage
   */
  async getApplication(appId: string): Promise<Application> {
    // E2E TEST MODE: Return mock application
    if (isE2ETestMode()) {
      const store = getE2EMockStore();
      const app = store.applications.find((a) => a.appId === appId);
      if (app) {
        return app;
      }
      throw new ApiError(`Application not found: ${appId}`, 404);
    }

    return this.get<Application>(`/applications/${appId}`);
  }

  /**
   * 4. Update Application
   * PUT /applications/{app_id}
   * Authorization: app_admin role (own org apps only)
   *
   * E2E MODE: Updates mock application in localStorage
   */
  async updateApplication(appId: string, updates: UpdateApplicationRequest): Promise<Application> {
    // E2E TEST MODE: Update mock application
    if (isE2ETestMode()) {
      const store = getE2EMockStore();
      const index = store.applications.findIndex((a) => a.appId === appId);
      if (index === -1) {
        throw new ApiError(`Application not found: ${appId}`, 404);
      }

      const updatedApp: Application = {
        ...store.applications[index],
        ...updates,
        updatedAt: new Date().toISOString(),
      };
      store.applications[index] = updatedApp;
      saveE2EMockStore(store);
      return updatedApp;
    }

    return this.put<Application>(`/applications/${appId}`, updates);
  }

  /**
   * 5. Delete Application
   * DELETE /applications/{app_id}
   * Authorization: app_admin role (own org apps only)
   *
   * E2E MODE: Deletes mock application from localStorage
   */
  async deleteApplication(appId: string): Promise<void> {
    // E2E TEST MODE: Delete mock application
    if (isE2ETestMode()) {
      const store = getE2EMockStore();
      const index = store.applications.findIndex((a) => a.appId === appId);
      if (index === -1) {
        throw new ApiError(`Application not found: ${appId}`, 404);
      }

      store.applications.splice(index, 1);
      saveE2EMockStore(store);
      return;
    }

    return this.delete<void>(`/applications/${appId}`);
  }

  /**
   * 5b. Archive Application
   * PUT /applications/{app_id} with status: 'archived'
   * Authorization: app_admin role (own org apps only)
   *
   * Soft-archives an application by setting status to 'archived'.
   * The application remains in the database but is hidden from default views.
   */
  async archiveApplication(appId: string): Promise<Application> {
    return this.updateApplication(appId, { status: ApplicationStatus.Archived });
  }

  /**
   * 5c. Unarchive Application
   * PUT /applications/{app_id} with status: 'hidden'
   * Authorization: app_admin role (own org apps only)
   *
   * Restores an archived application by setting status to 'hidden'.
   */
  async unarchiveApplication(appId: string): Promise<Application> {
    return this.updateApplication(appId, { status: ApplicationStatus.Hidden });
  }

  // ============================================================================
  // Asset Management APIs
  // ============================================================================

  /**
   * 6. Upload Application Icon
   * POST /applications/{app_id}/icon
   * Authorization: app_admin role (own org apps only)
   */
  async uploadIcon(appId: string, file: File): Promise<UploadAssetResponse> {
    return this.uploadFile(`/applications/${appId}/icon`, file);
  }

  /**
   * 7. Upload Screenshot
   * POST /applications/{app_id}/screenshots
   * Authorization: app_admin role (own org apps only)
   */
  async uploadScreenshot(appId: string, file: File): Promise<UploadAssetResponse> {
    return this.uploadFile(`/applications/${appId}/screenshots`, file);
  }

  /**
   * 8. Get Icon Presigned URL
   * POST /applications/{app_id}/icon/presigned-url
   * Authorization: app_admin role (own org apps only)
   *
   * Returns a presigned URL for direct client-side S3 upload
   */
  async getIconPresignedUrl(
    appId: string,
    data: { filename: string; contentType: string }
  ): Promise<{ uploadUrl: string; cloudfrontUrl: string; expiresIn: number; key: string }> {
    return this.post(`/applications/${appId}/icon/presigned-url`, data);
  }

  /**
   * 9. Get Screenshots Presigned URLs
   * POST /applications/{app_id}/screenshots/presigned-url
   * Authorization: app_admin role (own org apps only)
   *
   * Returns presigned URLs for direct client-side S3 upload (up to 5 screenshots)
   */
  async getScreenshotsPresignedUrls(
    appId: string,
    data: { filename: string; contentType: string; count?: number }
  ): Promise<{
    urls: Array<{ uploadUrl: string; cloudfrontUrl: string; expiresIn: number; key: string }>;
  }> {
    return this.post(`/applications/${appId}/screenshots/presigned-url`, data);
  }

  // ============================================================================
  // TOS Management APIs (GW-11433/GW-11452, PRP-MKTPL-016)
  //
  // Renamed to versioned `/v1/applications/{appId}/tos/*` routes and migrated
  // to the canonical split-endpoint acceptance shape shared with
  // datasets/services/missions TOS (see the ApplicationTOS* types above).
  // Replaces the prior unversioned combined-shape endpoints — no compat
  // shim, per this repo's convention, since gw-functions' backend
  // (GW-11433) has already migrated.
  // ============================================================================

  /**
   * Check TOS acceptance status (staleness-aware)
   * GET /v1/applications/{app_id}/tos/acceptance
   * Authorization: Any authenticated user
   */
  async checkTOSAcceptance(appId: string): Promise<ApplicationTOSAcceptanceStatus> {
    return this.get<ApplicationTOSAcceptanceStatus>(`/v1/applications/${appId}/tos/acceptance`);
  }

  /**
   * Accept TOS as an individual user
   * POST /v1/applications/{app_id}/tos/accept/user
   * Authorization: Any authenticated user
   *
   * @param tosVersion - The application's current TOS version being accepted
   */
  async acceptTOSUser(appId: string, tosVersion: number): Promise<ApplicationTOSAcceptance> {
    return this.post<ApplicationTOSAcceptance>(`/v1/applications/${appId}/tos/accept/user`, {
      tosVersion,
    });
  }

  /**
   * Accept TOS on behalf of the organization (org_admin/global_admin only)
   * POST /v1/applications/{app_id}/tos/accept/org
   * Authorization: org_admin role
   *
   * @param tosVersion - The application's current TOS version being accepted
   */
  async acceptTOSOrg(appId: string, tosVersion: number): Promise<ApplicationTOSAcceptance> {
    return this.post<ApplicationTOSAcceptance>(`/v1/applications/${appId}/tos/accept/org`, {
      tosVersion,
    });
  }

  /**
   * List TOS acceptances for this application (FR-29, org_admin/global_admin only)
   * GET /v1/applications/{app_id}/tos/acceptances
   * Authorization: org_admin role
   */
  async listTOSAcceptances(appId: string): Promise<ApplicationTOSAcceptancesResponse> {
    return this.get<ApplicationTOSAcceptancesResponse>(`/v1/applications/${appId}/tos/acceptances`);
  }

  // ============================================================================
  // Session Management APIs
  // ============================================================================

  /**
   * 11. Create Session
   * POST /applications/{app_id}/sessions
   * Authorization: Any authenticated user
   */
  async createSession(appId: string, data: CreateSessionRequest): Promise<ApplicationSession> {
    return this.post<ApplicationSession>(`/applications/${appId}/sessions`, data);
  }

  /**
   * 12. List User Sessions (for specific application)
   * GET /applications/{app_id}/sessions?status={status}
   * Authorization: Any authenticated user
   */
  async listUserSessions(appId: string, filters?: SessionFilters): Promise<ApplicationSession[]> {
    const queryParams = filters ? this.buildQueryString(filters as Record<string, string>) : '';
    const response = await this.get<ListSessionsResponse>(
      `/applications/${appId}/sessions${queryParams}`
    );
    return response.sessions;
  }

  /**
   * 12b. List All User Sessions (across all applications)
   * GET /sessions?status={status}&applicationId={appId}
   * Authorization: Any authenticated user
   */
  async listAllUserSessions(filters?: SessionFilters): Promise<ApplicationSession[]> {
    const queryParams = filters ? this.buildQueryString(filters as Record<string, string>) : '';
    const fullUrl = `/sessions${queryParams}`;

    const response = await this.get<ListSessionsResponse>(fullUrl);
    return response.sessions;
  }

  /**
   * 13. Revoke Session
   * DELETE /applications/{app_id}/sessions/{session_id}
   * Authorization: Session owner OR org_admin
   */
  async revokeSession(appId: string, sessionId: string): Promise<void> {
    return this.delete<void>(`/applications/${appId}/sessions/${sessionId}`);
  }

  // ============================================================================
  // Organization Admin APIs
  // ============================================================================

  /**
   * 14. Enable/Disable Application
   * PUT /organizations/{org_id}/applications/{app_id}/enabled
   * Authorization: org_admin role
   */
  async setApplicationEnabled(
    orgId: string,
    appId: string,
    data: EnableApplicationRequest
  ): Promise<OrganizationAppLimit> {
    assertOrgId(orgId);
    return this.put<OrganizationAppLimit>(
      `/organizations/${orgId}/applications/${appId}/enabled`,
      data
    );
  }

  /**
   * 16. Get Usage Statistics
   * GET /organizations/{org_id}/applications/{app_id}/usage?month={YYYY-MM}
   * Authorization: org_admin role
   */
  async getUsageStatistics(
    orgId: string,
    appId: string,
    month?: string
  ): Promise<ApplicationUsageStats> {
    assertOrgId(orgId);
    const queryParams = month ? this.buildQueryString({ month }) : '';
    return this.get<ApplicationUsageStats>(
      `/organizations/${orgId}/applications/${appId}/usage${queryParams}`
    );
  }

  /**
   * 17. List Organization Applications
   * GET /organizations/{org_id}/applications?limit={limit}&cursor={cursor}&search={search}&status={status}
   * Authorization: org_admin role
   */
  async listOrgApplications(
    orgId: string,
    filters?: ApplicationFilters
  ): Promise<ListApplicationsResponse> {
    assertOrgId(orgId);
    // E2E TEST MODE: Return mock applications from localStorage
    if (isE2ETestMode()) {
      const store = getE2EMockStore();
      // Filter by orgId and apply search filter if provided
      // In E2E mode, be lenient with org ID matching - if app has test-org-001 or starts with test-org, match it
      let filteredApps = store.applications.filter(
        (app) =>
          app.providerOrgId === orgId ||
          (orgId.startsWith('test-org') && app.providerOrgId?.startsWith('test-org'))
      );

      if (filters?.search) {
        const searchLower = filters.search.toLowerCase();
        filteredApps = filteredApps.filter(
          (app) =>
            app.name.toLowerCase().includes(searchLower) ||
            app.description?.toLowerCase().includes(searchLower)
        );
      }

      if (filters?.status) {
        filteredApps = filteredApps.filter((app) => app.status === filters.status);
      }

      return {
        applications: filteredApps,
        nextCursor: undefined,
      };
    }

    const queryParams = filters ? this.buildQueryString(filters as Record<string, string>) : '';
    return this.get<ListApplicationsResponse>(`/organizations/${orgId}/applications${queryParams}`);
  }

  // ============================================================================
  // Organization TOS Overview APIs
  // ============================================================================

  /**
   * GW-1918/GW-11433: Get Organization TOS Overview (FR-29 cross-item read)
   * GET /v1/applications/tos/org-overview
   * Authorization: org_admin role
   *
   * Returns an overview of all applications requiring org-level TOS acceptance,
   * including staleness (orgNeedsReaccept) and summary counts.
   */
  async getOrgTOSOverview(): Promise<ApplicationOrgTOSOverviewResponse> {
    return this.get<ApplicationOrgTOSOverviewResponse>('/v1/applications/tos/org-overview');
  }

  // ============================================================================
  // Audience Management APIs (GW-6741)
  // ============================================================================

  /**
   * Set org-allowlist audience on an application
   * PUT /applications/{app_id} with audience: { type: 'ORG_ALLOWLIST', orgIds }
   */
  async updateOrgAllowlist(appId: string, orgIds: string[]): Promise<Application> {
    return this.put<Application>(`/applications/${appId}`, {
      audience: {
        type: 'ORG_ALLOWLIST',
        orgIds,
      },
    });
  }

  /**
   * Update audience grants for an application
   * PUT /applications/{app_id} with nested audience object
   * Authorization: app_admin role (own org apps only)
   *
   * GW-11058/GW-11059: clearing all grants sends an explicit `audience: null`
   * clear signal so the backend REMOVEs the restriction (making the app
   * unrestricted). Sending an empty `GRANT_REQUIRED` object instead would be
   * ambiguous — the backend nil-guard could treat it as "unchanged" and
   * silently retain the previous restriction.
   */
  async updateGrants(appId: string, grants: string[]): Promise<Application> {
    if (grants.length === 0) {
      return this.clearAudience(appId);
    }
    return this.put<Application>(`/applications/${appId}`, {
      audience: {
        type: 'GRANT_REQUIRED',
        grants: grants.map((name) => ({ name })),
      },
    });
  }

  /**
   * Clear all audience restrictions on an application, making it unrestricted.
   * PUT /applications/{app_id} with `audience: null`
   * Authorization: app_admin role (own org apps only)
   *
   * GW-11058/GW-11059: `null` is the explicit clear signal. The backend
   * distinguishes it from an omitted `audience` (which retains the stored
   * value) and REMOVEs the attribute.
   */
  async clearAudience(appId: string): Promise<Application> {
    return this.put<Application>(`/applications/${appId}`, {
      audience: null,
    });
  }

  // ============================================================================
  // Name Validation APIs
  // ============================================================================

  /**
   * 18. Check Name Availability
   * GET /applications/name-available?name={name}&excludeAppId={excludeAppId}
   * Authorization: Any authenticated user
   *
   * GW-146: Real-time name validation for application forms.
   * Returns availability status and suggestions if name is taken.
   *
   * @param name - The application name to check
   * @param excludeAppId - Optional app ID to exclude (for edit mode)
   */
  async checkNameAvailability(
    name: string,
    excludeAppId?: string
  ): Promise<NameAvailabilityResponse> {
    const params: Record<string, string> = { name };
    if (excludeAppId) {
      params.excludeAppId = excludeAppId;
    }
    const queryParams = this.buildQueryString(params);
    return this.get<NameAvailabilityResponse>(`/applications/name-available${queryParams}`);
  }

  // ============================================================================
  // Public JWKS Endpoint
  // ============================================================================

  /**
   * 19. Get Public Keys
   * GET /.well-known/jwks.json
   * Authorization: None (public)
   */
  async getPublicKeys(): Promise<{ keys: unknown[] }> {
    const response = await fetch(`${this.baseURL}/.well-known/jwks.json`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new ApiError(`Failed to fetch JWKS: ${response.statusText}`, response.status);
    }

    return response.json();
  }
}

// Export singleton instance
export const applicationsApi = new ApplicationsApiClient();
